export type PrescriptionSource = 'cash' | 'wasfaty' | 'insurance' | 'outpatient' | 'yusr'
export type PrescriptionStatus = 'in-progress' | 'awaiting-approval' | 'ready' | 'out-for-delivery' | 'delivered' | 'needs-contact' | 'expired' | 'insurance-rejected' | 'cancelled'

export interface PrescriptionRecord {
  id: string
  date: string
  status: PrescriptionStatus
  source: PrescriptionSource
  patientName: string
  items: Array<{ name: string; quantity: number; price: number; image: string }>
  doctor?: string
  deliveryAddress?: string
  paymentMethod?: string
  total: number
  insuranceProvider?: string
  wasfatyNumber?: string
  externalReference?: string
  trackingHistory: Array<{ status: string; timestamp: string; description: string }>
}

export const prescriptionSourceLabels: Record<PrescriptionSource, string> = {
  cash: 'Cash prescription',
  wasfaty: 'Wasfaty',
  insurance: 'Insurance prescription',
  outpatient: 'Outpatient clinic',
  yusr: 'Yusr prescription',
}

export const prescriptionStatusLabels: Record<PrescriptionStatus, string> = {
  'in-progress': 'Pharmacist review',
  'awaiting-approval': 'Awaiting insurance approval',
  ready: 'Ready for fulfillment',
  'out-for-delivery': 'Out for delivery',
  delivered: 'Delivered',
  'needs-contact': 'Needs your attention',
  expired: 'Prescription expired',
  'insurance-rejected': 'Insurance claim rejected',
  cancelled: 'Cancelled',
}

export const prescriptionTimeline = [
  'Request submitted',
  'Pharmacist review',
  'Approval confirmed',
  'Preparing medicines',
  'Ready for fulfillment',
] as const

export const prescriptionRecords: PrescriptionRecord[] = [
  {
    id: '#RX-2025-00123', date: '2025-01-15', status: 'delivered', source: 'cash', patientName: 'Ahmed Mohammed', doctor: 'Dr. Sarah Ahmed', deliveryAddress: 'Al Malqa, Riyadh', paymentMethod: 'Mada', total: 245.5,
    items: [{ name: 'Metformin 500mg', quantity: 60, price: 125.5, image: 'https://img.innovapharmacy.com/media/catalog/product/4/5/4537b3e3-4206-41fc-b34e-53ddce591f66_1.jpg' }, { name: 'Lisinopril 10mg', quantity: 30, price: 120, image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1015683_1.jpg' }],
    trackingHistory: [{ status: 'Delivered', timestamp: '2025-01-15T14:30:00', description: 'Delivered successfully to the selected address.' }, { status: 'Out for delivery', timestamp: '2025-01-15T10:00:00', description: 'Courier is on the way.' }],
  },
  {
    id: '#RX-2025-00122', date: '2025-01-10', status: 'ready', source: 'wasfaty', patientName: 'Ahmed Mohammed', doctor: 'Dr. Mohammed Ali', wasfatyNumber: 'WF-2025-001234', paymentMethod: 'Covered by Wasfaty', total: 0,
    items: [{ name: 'Atorvastatin 20mg', quantity: 30, price: 89.99, image: 'https://img.innovapharmacy.com/media/catalog/product/2/1/213099.jpg' }],
    trackingHistory: [{ status: 'Ready for pickup', timestamp: '2025-01-10T12:00:00', description: 'Your medicines are ready at Riyadh Park branch.' }],
  },
  {
    id: '#RX-2025-00121', date: '2025-01-05', status: 'awaiting-approval', source: 'insurance', patientName: 'Ahmed Mohammed', doctor: 'Dr. Fatima Hassan', insuranceProvider: 'Bupa Arabia', deliveryAddress: 'Al Malqa, Riyadh', paymentMethod: 'Insurance pending', total: 456,
    items: [{ name: 'Insulin Glargine', quantity: 1, price: 356, image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1030868_1.png' }, { name: 'Test Strips', quantity: 100, price: 100, image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1000157_1.jpg' }],
    trackingHistory: [{ status: 'Awaiting insurer approval', timestamp: '2025-01-05T14:00:00', description: 'We submitted this prescription for approval.' }],
  },
  {
    id: '#RX-2025-00120', date: '2025-01-03', status: 'in-progress', source: 'outpatient', patientName: 'Sara Mohammed', doctor: 'Dr. Layla Hassan', externalReference: 'CLN-88421', deliveryAddress: 'Al Yasmin, Riyadh', paymentMethod: 'Cash on delivery', total: 182.5,
    items: [{ name: 'Amoxicillin 500mg', quantity: 21, price: 82.5, image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1015683_1.jpg' }, { name: 'Probiotic capsules', quantity: 1, price: 100, image: 'https://img.innovapharmacy.com/media/catalog/product/4/5/4537b3e3-4206-41fc-b34e-53ddce591f66_1.jpg' }],
    trackingHistory: [{ status: 'Pharmacist review', timestamp: '2025-01-03T16:20:00', description: 'Clinic prescription is being reviewed.' }],
  },
  {
    id: '#RX-2025-00119', date: '2024-12-28', status: 'out-for-delivery', source: 'yusr', patientName: 'Ahmed Mohammed', externalReference: 'YSR-554820', deliveryAddress: 'Al Quds, Riyadh', paymentMethod: 'Yusr benefit', total: 45,
    items: [{ name: 'Vitamin D3 Drops', quantity: 1, price: 45, image: 'https://img.innovapharmacy.com/media/catalog/product/2/1/213099.jpg' }],
    trackingHistory: [{ status: 'Out for delivery', timestamp: '2024-12-28T11:45:00', description: 'Your order has left the pharmacy.' }],
  },
  {
    id: '#RX-2025-00118', date: '2024-12-23', status: 'expired', source: 'cash', patientName: 'Ahmed Mohammed', doctor: 'Dr. Sarah Ahmed', deliveryAddress: 'Al Yasmin, Riyadh', paymentMethod: 'Pending renewal', total: 125.5,
    items: [{ name: 'Lisinopril 10mg', quantity: 30, price: 125.5, image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1015683_1.jpg' }],
    trackingHistory: [{ status: 'Prescription expired', timestamp: '2024-12-23T10:15:00', description: 'Upload a renewed prescription to continue.' }],
  },
  {
    id: '#RX-2025-00117', date: '2024-12-20', status: 'insurance-rejected', source: 'insurance', patientName: 'Ahmed Mohammed', doctor: 'Dr. Fatima Hassan', insuranceProvider: 'Bupa Arabia', deliveryAddress: 'Al Malqa, Riyadh', paymentMethod: 'Insurance claim rejected', total: 210,
    items: [{ name: 'Insulin test strips', quantity: 1, price: 210, image: 'https://img.innovapharmacy.com/media/catalog/product/1/0/1000157_1.jpg' }],
    trackingHistory: [{ status: 'Insurance claim rejected', timestamp: '2024-12-20T15:40:00', description: 'Choose another payment method or contact your insurer.' }],
  },
]
